import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@heidi/utils';

import { useOpenChangeComplete } from '../hooks/use-open-change-complete';
import {
  drawerCloseIconClasses,
  drawerContentBaseClasses,
  drawerContentFixedPositionClasses,
  drawerContentPortalPositionClasses,
  drawerDescriptionClasses,
  drawerFooterClasses,
  drawerHeaderClasses,
  drawerOverlayClasses,
  drawerTitleClasses,
} from './drawer.styles';
import type { DrawerContentProps, DrawerRootProps } from './drawer.types';

/**
 * Drawer wrapper around Vaul.
 *
 * Vaul's `direction` prop (`'top' | 'bottom' | 'left' | 'right'`) is
 * strictly physical and doesn't respect RTL as Vaul never reads `<html dir>`.
 *
 * If you ever need a side-mounted drawer, use `<Sheet>` instead.
 */
const DrawerAnimatedElementContext = React.createContext<
  React.RefObject<HTMLElement | null> | undefined
>(undefined);

const Drawer = ({
  onOpenChange,
  onOpenChangeComplete,
  open,
  shouldScaleBackground = true,
  ...props
}: DrawerRootProps) => {
  const animatedElementRef = React.useRef<HTMLElement | null>(null);

  useOpenChangeComplete({
    onOpenChangeComplete,
    open,
    ref: animatedElementRef,
  });

  return (
    <DrawerAnimatedElementContext.Provider value={animatedElementRef}>
      <DrawerPrimitive.Root
        onOpenChange={onOpenChange}
        open={open}
        shouldScaleBackground={shouldScaleBackground}
        {...props}
      />
    </DrawerAnimatedElementContext.Provider>
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(drawerOverlayClasses, className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      overlayClassName,
      hideCloseIcon,
      hideOverlay = false,
      closeIconClassName,
      portalContainer,
      ...props
    },
    ref
  ) => {
    const animatedElementRef = React.useContext(DrawerAnimatedElementContext);
    const composedRef = useComposedRefs(ref, animatedElementRef);

    return (
      <DrawerPortal container={portalContainer ?? undefined}>
        {!hideOverlay && !portalContainer && (
          <DrawerOverlay className={overlayClassName} />
        )}
        <DrawerPrimitive.Content
          ref={composedRef}
          className={cn(
            portalContainer
              ? drawerContentPortalPositionClasses
              : drawerContentFixedPositionClasses,
            drawerContentBaseClasses,
            className
          )}
          {...props}
        >
          {!hideCloseIcon && (
            <div className={cn(drawerCloseIconClasses, closeIconClassName)} />
          )}
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(drawerHeaderClasses, className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(drawerFooterClasses, className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(drawerTitleClasses, className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn(drawerDescriptionClasses, className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
